import { useCallback, useState } from 'react';

import { safeStorage } from './safeStorage';

interface StoredDashboardTableColumnPreferences {
  hidden: string[];
}

export function useDashboardTableColumnPreferences(
  storageKey: string,
  columnIds: readonly string[],
  defaultHidden: readonly string[] = [],
  storage: Storage | null = safeStorage(),
  lockedIds: readonly string[] = [],
) {
  const [hidden, setHiddenState] = useState<ReadonlySet<string>>(() =>
    readStoredHidden(storage, storageKey, columnIds, lockedIds, defaultHidden),
  );

  const setVisible = useCallback(
    (id: string, visible: boolean) => {
      if (lockedIds.includes(id)) return;

      const next = new Set(hidden);
      if (visible) next.delete(id);
      else next.add(id);
      setHiddenState(next);
      writeStoredHidden(storage, storageKey, next);
    },
    [hidden, lockedIds, storage, storageKey],
  );

  const setHidden = useCallback(
    (ids: readonly string[]) => {
      const next = filterHiddenIds(ids, columnIds, lockedIds);
      setHiddenState(next);
      writeStoredHidden(storage, storageKey, next);
    },
    [columnIds, lockedIds, storage, storageKey],
  );

  const reset = useCallback(() => {
    setHiddenState(filterHiddenIds(defaultHidden, columnIds, lockedIds));
    clearStoredHidden(storage, storageKey);
  }, [columnIds, defaultHidden, lockedIds, storage, storageKey]);

  return { hidden, setVisible, setHidden, reset };
}

function filterHiddenIds(
  ids: readonly string[],
  columnIds: readonly string[],
  lockedIds: readonly string[],
): ReadonlySet<string> {
  const validIds = new Set(columnIds);
  const lockedIdSet = new Set(lockedIds);
  return new Set(ids.filter((id) => validIds.has(id) && !lockedIdSet.has(id)));
}

function readStoredHidden(
  storage: Storage | null,
  storageKey: string,
  columnIds: readonly string[],
  lockedIds: readonly string[],
  defaultHidden: readonly string[],
): ReadonlySet<string> {
  const raw = readRawItem(storage, storageKey);
  if (raw === null) return filterHiddenIds(defaultHidden, columnIds, lockedIds);

  const parsedHidden = parseStoredHidden(raw);
  return filterHiddenIds(parsedHidden ?? defaultHidden, columnIds, lockedIds);
}

function parseStoredHidden(raw: string): string[] | null {
  try {
    const parsed = JSON.parse(raw) as Partial<StoredDashboardTableColumnPreferences>;
    return Array.isArray(parsed.hidden) ? parsed.hidden : null;
  } catch {
    return null;
  }
}

function readRawItem(storage: Storage | null, storageKey: string): string | null {
  try {
    return storage?.getItem(storageKey) ?? null;
  } catch {
    return null;
  }
}

function writeStoredHidden(
  storage: Storage | null,
  storageKey: string,
  hidden: ReadonlySet<string>,
) {
  try {
    const payload: StoredDashboardTableColumnPreferences = { hidden: [...hidden] };
    storage?.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Keep the in-memory preference when storage is unavailable.
  }
}

function clearStoredHidden(storage: Storage | null, storageKey: string) {
  try {
    storage?.removeItem(storageKey);
  } catch {
    // State is already reset in memory.
  }
}
